import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import LoopBuilder from "@/components/LoopBuilder";
import ApplyFromMatch from "@/components/ApplyFromMatch";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Waypoints, MapPin, Briefcase, GraduationCap, Target, Layers, Inbox,
  RefreshCw, Loader2, Search, Building2, ExternalLink, Mail, Globe, Pencil, Trash2,
  Calendar, Factory, KeyRound, Sparkles,
} from "lucide-react";

const Field = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-500">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="mt-1.5 text-slate-900 font-medium break-words">{value || "—"}</div>
  </div>
);

export default function LoopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  const [jobs, setJobs] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState(null);
  const [jobQuery, setJobQuery] = useState("");
  const [jobSort, setJobSort] = useState("recent");
  const [activeJob, setActiveJob] = useState(null);

  const [matches, setMatches] = useState(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState(null);
  const [matchQuery, setMatchQuery] = useState("");
  const [activeMatch, setActiveMatch] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [applyMatch, setApplyMatch] = useState(null);
  const [autoSyncing, setAutoSyncing] = useState(false);

  const loadLoop = useCallback(async () => {
    try {
      const [cfg, res] = await Promise.all([api.get("/loops/config"), api.get(`/loops/${id}`)]);
      setConfig(cfg.data);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadLoop(); }, [loadLoop]);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true); setJobsError(null);
    try {
      const { data } = await api.get(`/loops/${id}/jobs`);
      setJobs(data.jobs);
    } catch (e) {
      setJobsError(formatApiError(e.response?.data?.detail));
    } finally {
      setJobsLoading(false);
    }
  }, [id]);

  const loadMatches = useCallback(async () => {
    setMatchesLoading(true); setMatchesError(null);
    try {
      const { data } = await api.get(`/loops/${id}/matches`);
      setMatches(data.matches);
    } catch (e) {
      setMatchesError(formatApiError(e.response?.data?.detail));
    } finally {
      setMatchesLoading(false);
    }
  }, [id]);

  const onTab = (v) => {
    if (v === "jobs" && jobs === null && !jobsLoading) loadJobs();
    if (v === "matches" && matches === null && !matchesLoading) loadMatches();
  };

  const filteredJobs = useMemo(() => {
    if (!jobs) return [];
    let out = jobs.filter((j) => {
      const q = jobQuery.toLowerCase();
      return !q || `${j.title} ${j.company} ${j.location}`.toLowerCase().includes(q);
    });
    if (jobSort === "recent") {
      out = [...out].sort((a, b) => new Date(b.createdAtDate || 0) - new Date(a.createdAtDate || 0));
    } else if (jobSort === "title") {
      out = [...out].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    } else if (jobSort === "company") {
      out = [...out].sort((a, b) => (a.company || "").localeCompare(b.company || ""));
    }
    return out;
  }, [jobs, jobQuery, jobSort]);

  const filteredMatches = useMemo(() => {
    if (!matches) return [];
    const q = matchQuery.toLowerCase();
    return matches.filter((m) => !q || `${m.title} ${m.company} ${m.location}`.toLowerCase().includes(q));
  }, [matches, matchQuery]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/loops/${id}`);
      toast.success("Loop deleted.");
      navigate("/app/loops");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const toggleAutoSync = async (enabled) => {
    setAutoSyncing(true);
    try {
      const { data } = await api.patch(`/loops/${id}/auto-sync`, { enabled });
      setData((prev) => ({ ...prev, loop: data.loop }));
      toast.success(enabled ? "Auto-sync enabled — counts refresh on schedule." : "Auto-sync disabled.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAutoSyncing(false);
    }
  };

  if (loading) {
    return <div className="py-20 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-500" /></div>;
  }
  if (!data) return null;

  const loop = data.loop;
  const remote = data.loopcv_config;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="loop-detail-page">
      <button onClick={() => navigate("/app/loops")} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm" data-testid="back-to-loops">
        <ArrowLeft className="w-4 h-4" /> All loops
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-amber-500 text-xs font-mono uppercase tracking-widest">
              <Waypoints className="w-4 h-4" /> Loop Command Centre
            </div>
            <h1 className="text-2xl font-bold tracking-tight font-display mt-2">{loop.title}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-xs">
              <span className="flex items-center gap-1.5 text-slate-500"><MapPin className="w-3.5 h-3.5" /> {loop.location}</span>
              <span className="flex items-center gap-1.5 text-slate-500"><Briefcase className="w-3.5 h-3.5" /> {loop.type}</span>
              <span className="flex items-center gap-1.5 text-slate-500"><GraduationCap className="w-3.5 h-3.5" /> {loop.level}</span>
              <span className="flex items-center gap-1.5 text-amber-400"><Target className="w-3.5 h-3.5" /> Match {loop.matchlevel ?? "—"}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="text-right text-xs font-mono text-slate-500 mr-2">
              <div>Engine: <span className="text-slate-700">{loop.loop_id || "—"}</span></div>
              <div>Apply Series: <span className="text-slate-700">{loop.external_id}</span></div>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" onValueChange={onTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Overview</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Collected Jobs</TabsTrigger>
          <TabsTrigger value="matches" data-testid="tab-matches" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Matches</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Loop Settings</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-5 space-y-5" data-testid="overview-tab">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-emerald-300"><Layers className="w-4 h-4" /><span className="text-2xl font-bold font-display">{loop.collected_count ?? "—"}</span></div>
              <p className="text-xs text-slate-500 mt-1">Collected jobs (last sync)</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-amber-300"><Inbox className="w-4 h-4" /><span className="text-2xl font-bold font-display">{loop.matched_count ?? "—"}</span></div>
              <p className="text-xs text-slate-500 mt-1">Matched jobs (last sync)</p>
            </div>
            <div className="col-span-2 rounded-2xl border border-slate-200 bg-white p-5 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Last sync</p>
                <p className="text-slate-800 mt-1 text-sm">{loop.last_synced_at ? new Date(loop.last_synced_at).toLocaleString() : "Never synced"}</p>
              </div>
              <Button data-testid="overview-refresh" onClick={() => { loadJobs(); loadMatches(); loadLoop(); }}
                className="bg-amber-500 text-black hover:bg-amber-400 font-semibold"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-3">Active search configuration</h3>
            {data.remote_error ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
                <KeyRound className="w-4 h-4 mt-0.5" /> {data.remote_error}
              </div>
            ) : remote ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field icon={Waypoints} label="Title" value={remote.title} />
                <Field icon={MapPin} label="Location" value={remote.location} />
                <Field icon={Briefcase} label="Type" value={remote.type} />
                <Field icon={GraduationCap} label="Level" value={remote.level} />
                <Field icon={Target} label="Match level" value={remote.matchlevel ?? loop.matchlevel} />
                <Field icon={Search} label="Keywords" value={(remote.keywords || []).join(", ")} />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Field icon={Waypoints} label="Title" value={loop.title} />
                <Field icon={MapPin} label="Location" value={loop.location} />
                <Field icon={Briefcase} label="Type" value={loop.type} />
                <Field icon={GraduationCap} label="Level" value={loop.level} />
                <Field icon={Target} label="Match level" value={loop.matchlevel} />
                <Field icon={Search} label="Keywords" value={(loop.keywords || []).join(", ")} />
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {loop.scrapers?.map((s) => <Badge key={s} className="bg-orange-500/10 text-orange-300 border border-orange-500/30">{s}</Badge>)}
          </div>
        </TabsContent>

        {/* Collected Jobs */}
        <TabsContent value="jobs" className="mt-5" data-testid="jobs-tab">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="jobs-search" value={jobQuery} onChange={(e) => setJobQuery(e.target.value)}
                placeholder="Filter by title, company, location…" className="pl-9 bg-slate-50 border-slate-300 h-10" />
            </div>
            <Select value={jobSort} onValueChange={setJobSort}>
              <SelectTrigger className="w-[160px] bg-slate-50 border-slate-300 h-10" data-testid="jobs-sort"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-800">
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="title">Title A–Z</SelectItem>
                <SelectItem value="company">Company A–Z</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadJobs} disabled={jobsLoading} className="border-slate-300 h-10" data-testid="reload-jobs">
              <RefreshCw className={`w-4 h-4 ${jobsLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {jobsError ? (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{jobsError}</div>
          ) : jobsLoading ? (
            <div className="py-16 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
          ) : filteredJobs.length === 0 ? (
            <div className="py-16 text-center text-slate-500"><Layers className="w-10 h-10 mx-auto opacity-40" /><p className="mt-3 text-sm">No jobs collected for this search yet.</p></div>
          ) : (
            <div className="space-y-3" data-testid="jobs-list">
              <p className="text-xs text-slate-500">{filteredJobs.length} collected job(s)</p>
              {filteredJobs.map((j) => (
                <button key={j._id} onClick={() => setActiveJob(j)} data-testid={`job-row-${j._id}`}
                  className="w-full text-left rounded-xl border border-slate-200 bg-slate-50 hover:border-orange-500/40 p-4 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{j.title}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Building2 className="w-3.5 h-3.5" /> {j.company} · {j.location}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {j.platform && <Badge className="bg-slate-100 text-slate-700 border border-slate-600">{j.platform}</Badge>}
                      {j.createdAtDate && <span className="text-xs text-slate-500">{new Date(j.createdAtDate).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Matches */}
        <TabsContent value="matches" className="mt-5" data-testid="matches-tab">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 mb-4 text-xs text-slate-500">
            Matches are collected jobs that passed the loop's criteria (location, title, keywords). They may include an apply link, contact email and company domain.
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="matches-search" value={matchQuery} onChange={(e) => setMatchQuery(e.target.value)}
                placeholder="Filter matches…" className="pl-9 bg-slate-50 border-slate-300 h-10" />
            </div>
            <Button variant="outline" onClick={loadMatches} disabled={matchesLoading} className="border-slate-300 h-10" data-testid="reload-matches">
              <RefreshCw className={`w-4 h-4 ${matchesLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {matchesError ? (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{matchesError}</div>
          ) : matchesLoading ? (
            <div className="py-16 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
          ) : filteredMatches.length === 0 ? (
            <div className="py-16 text-center text-slate-500"><Inbox className="w-10 h-10 mx-auto opacity-40" /><p className="mt-3 text-sm">No matches for this search yet.</p></div>
          ) : (
            <div className="space-y-3" data-testid="matches-list">
              <p className="text-xs text-slate-500">{filteredMatches.length} match(es)</p>
              {filteredMatches.map((m) => (
                <div key={m._id} data-testid={`match-row-${m._id}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium">{m.title}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Building2 className="w-3.5 h-3.5" /> {m.company} · {m.location}</p>
                    </div>
                    {m.job_type && <Badge className="bg-amber-500/10 text-amber-300 border border-amber-500/30 shrink-0">{m.job_type}</Badge>}
                  </div>
                  {m.summary && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{m.summary}</p>}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs">
                    {(m.applyLink || m.url) && (
                      <a href={m.applyLink || m.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-amber-400 hover:text-amber-200" data-testid={`match-apply-${m._id}`}>
                        <ExternalLink className="w-3.5 h-3.5" /> Apply link
                      </a>
                    )}
                    {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 text-slate-700 hover:text-amber-400 font-mono"><Mail className="w-3.5 h-3.5" /> {m.email}</a>}
                    {m.domain && <a href={m.domain} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-slate-500 hover:text-amber-400 font-mono"><Globe className="w-3.5 h-3.5" /> {m.domain.replace(/^https?:\/\//, "")}</a>}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between gap-3">
                    {m.email ? (
                      <Button size="sm" data-testid={`apply-match-${m._id}`} onClick={() => setApplyMatch(m)}
                        className="bg-orange-500 hover:bg-orange-400 text-white font-semibold">
                        <Sparkles className="w-4 h-4 mr-2" /> Apply with AI
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-500">No contact email on this match — use the apply link.</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Loop Settings */}
        <TabsContent value="settings" className="mt-5 space-y-5" data-testid="settings-tab">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 font-medium"><RefreshCw className="w-4 h-4 text-amber-500" /> Auto-Sync</div>
              <p className="text-sm text-slate-500 mt-1">Automatically refresh collected jobs & matches on a schedule so counts stay current.</p>
              {!config?.api_configured && <p className="text-xs text-amber-400/80 mt-1">The discovery engine must be connected by an administrator for this to take effect.</p>}
            </div>
            <Switch data-testid="auto-sync-switch" checked={!!loop.auto_sync} disabled={autoSyncing} onCheckedChange={toggleAutoSync} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field icon={Waypoints} label="Title" value={loop.title} />
            <Field icon={MapPin} label="Location" value={loop.location} />
            <Field icon={Briefcase} label="Type" value={loop.type} />
            <Field icon={GraduationCap} label="Level" value={loop.level} />
            <Field icon={Target} label="Match level" value={loop.matchlevel} />
            <Field icon={Search} label="Keywords" value={(loop.keywords || []).join(", ")} />
          </div>
          <div className="flex flex-wrap gap-2">
            {loop.scrapers?.map((s) => <Badge key={s} className="bg-orange-500/10 text-orange-300 border border-orange-500/30">{s}</Badge>)}
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="settings-edit-loop" onClick={() => setEditOpen(true)} disabled={config && !config.api_configured}
              className="bg-orange-500 hover:bg-orange-400 text-white font-semibold"><Pencil className="w-4 h-4 mr-2" /> Edit configuration</Button>
            <Button data-testid="settings-delete-loop" variant="outline" onClick={() => setDeleteOpen(true)}
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-4 h-4 mr-2" /> Delete loop</Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Job detail drawer */}
      <Sheet open={!!activeJob} onOpenChange={(o) => !o && setActiveJob(null)}>
        <SheetContent className="bg-white border-slate-200 text-slate-900 w-full sm:max-w-lg overflow-y-auto" data-testid="job-detail-drawer">
          {activeJob && (
            <>
              <SheetHeader><SheetTitle className="text-slate-900">{activeJob.title}</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-3 text-sm">
                <p className="flex items-center gap-2 text-slate-700"><Building2 className="w-4 h-4 text-slate-500" /> {activeJob.company}</p>
                <p className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-500" /> {activeJob.location}</p>
                <div className="flex flex-wrap gap-2">
                  {activeJob.job_type && <Badge className="bg-slate-100 text-slate-700 border border-slate-600">{activeJob.job_type}</Badge>}
                  {activeJob.level && <Badge className="bg-slate-100 text-slate-700 border border-slate-600">{activeJob.level}</Badge>}
                  {activeJob.platform && <Badge className="bg-orange-500/10 text-orange-300 border border-orange-500/30">{activeJob.platform}</Badge>}
                </div>
                {activeJob.industry && <p className="flex items-start gap-2 text-slate-500"><Factory className="w-4 h-4 text-slate-500 mt-0.5" /> {activeJob.industry}</p>}
                {activeJob.createdAtDate && <p className="flex items-center gap-2 text-slate-500"><Calendar className="w-4 h-4 text-slate-500" /> {new Date(activeJob.createdAtDate).toLocaleString()}</p>}
                {activeJob.summary && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Summary</p>
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{activeJob.summary}</p>
                  </div>
                )}
                {activeJob.url && (
                  <a href={activeJob.url} target="_blank" rel="noreferrer">
                    <Button className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold mt-2"><ExternalLink className="w-4 h-4 mr-2" /> View job posting</Button>
                  </a>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <LoopBuilder open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => loadLoop()} mode="edit" initial={loop} loopRef={loop.id} options={config} />

      <ApplyFromMatch open={!!applyMatch} onClose={() => setApplyMatch(null)} match={applyMatch} loopTitle={loop.title} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-white border-slate-200 text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this loop?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">Permanently deletes "{loop.title}" and stops its job collection.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-300 text-slate-700 hover:bg-slate-100">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-loop-detail" onClick={confirmDelete} disabled={deleting} className="bg-rose-500 text-white hover:bg-rose-400">
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
